import ReactMarkdown from 'react-markdown';
import Auth from './Auth';
import Landing from './Landing';
import AdminPanel from './AdminPanel';
import Layout from './components/Layout';
import AffiliatePage from './pages/AffiliatePage';
import Reports from './pages/Reports';
import SettingsPage from './pages/Settings';
import PricingPage from './pages/PricingPage';
import Community from './pages/Community';
import TradeEntry from './TradeEntry';
import AIInsights from './AIInsights';
import Pricing from './Pricing';
import PsychologyTracker from './PsychologyTracker';
import RiskManager from './RiskManager';
import AIAssistant from './AIAssistant';
import TermsAndConditions from './TermsAndConditions';
import PrivacyPolicy from './PrivacyPolicy';
import RefundPolicy from './RefundPolicy';
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { TrendingUp, TrendingDown, AlertTriangle, Award, Target, Brain, Upload, FileText, DollarSign, Bell, BellRing, Zap, Shield, BookOpen, Camera, Mic, MessageCircle, TrendingUpDown, CheckCircle, XCircle, Clock, BarChart3, Users, Trophy, Star, ThumbsUp, Settings, Play, Pause, Calendar, Activity, LogOut, ListChecks, Minimize2, Maximize2, CreditCard, Lock, Crown, ArrowRight, Sparkles, ChevronDown } from 'lucide-react';
import { getUserSubscription, hasActiveSubscription, getRemainingTrades, isInTrialPeriod } from './subscriptionUtils';
import { getPlanFromCache, setPlanCache, clearPlanCache } from './utils/planCache';
import { parseBrokerCSV } from './utils/csvParser';
import { buildSmartPrompt, buildPostTradePrompt } from './utils/smartPrompt';
import { auth } from './firebase';
import app from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getUserProfile, getTrades, getRiskSettings, saveUserProfile, saveTrade, serverTimestamp, updateStreak, getMonthlyStats, saveMonthlyStats, getWeeklyStats, saveWeeklyStats, saveAiInsight, getReportMeta, saveReportMeta } from './services/firestoreService';

// Emails that can access the Admin panel
const ADMIN_EMAILS = ['admin@tradefix.ai', 'krishautomation.services@gmail.com'];

// Returns ISO week key like "2026-W10" for a given date
const getISOWeekKey = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`;
};

const AdvancedTradingAnalyzer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Authentication State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Clear all user-specific data from state (called on logout or user switch)
  const clearUserData = () => {
    setTrades([]);
    setDashboardStats({ totalPnL: 0, winningTrades: 0, losingTrades: 0, winRate: 0 });
    setSubscription(null);
    setIsPaidUser(false);
    setTradesRemaining(5);
    setLiveAlerts([]);
    setAiCoachSuggestions([]);
    setStreak({ currentStreak: 0, longestStreak: 0 });
    setMonthlyProgress(null);
    setAiCoachPopup(null);
    setWeeklyReport(null);
    setMonthlyReport(null);
    setPersonalReport(null);
    setReportLoading(false);
    setPostTradeFeedback(null);
    setLossStreakAlert(null);
  };

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Clear previous user's data immediately before loading new user's data
        clearUserData();
        console.log('[Auth] User logged in, uid:', firebaseUser.uid);
        const profile = await getUserProfile(firebaseUser.uid);
        // Update lastLogin timestamp
        await saveUserProfile(firebaseUser.uid, { lastLogin: serverTimestamp() });
        const user = {
          uid: firebaseUser.uid,
          id: firebaseUser.uid,  // backward-compat alias
          email: firebaseUser.email,
          name: profile?.name || firebaseUser.displayName || 'User',
          createdAt: profile?.createdAt
        };
        setCurrentUser(user);
        setIsAuthenticated(true);
        setAuthReady(true);
        // Update login streak
        updateStreak(firebaseUser.uid).then(s => { if (s) setStreak(s); });
        // Redirect away from auth pages after login
        const path = window.location.pathname;
        if (path === '/login' || path === '/signup' || path === '/') {
          navigate('/app');
        }
      } else {
        console.log('[Auth] User logged out — clearing all state');
        clearUserData();
        setCurrentUser(null);
        setIsAuthenticated(false);
        setAuthReady(true);
      }
    });
    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Login — no-op; onAuthStateChanged handles state update
  const handleLogin = () => {};

  // Handle Logout
  const handleLogout = async () => {
    clearPlanCache();
    await signOut(auth);
    navigate('/login');
  };

  const [activeTab, setActiveTab] = useState('dashboard');

  // Listen for navigation events dispatched from child widgets (e.g. AIAssistant upgrade button)
  useEffect(() => {
    const handler = (e) => setActiveTab(e.detail);
    window.addEventListener('tradefix-navigate', handler);
    return () => window.removeEventListener('tradefix-navigate', handler);
  }, []);

  // Sync activeTab from URL path
  useEffect(() => {
    const pathToTab = {
      '/dashboard':   'dashboard',
      '/app':         'dashboard',
      '/trades':      'my-trades',
      '/journal':     'psychology',
      '/reports':     'reports',
      '/ai-analysis':   'ai-insights',
      '/risk-manager':  'risk',
      '/admin':         'admin',
      '/settings':      'settings',
      '/community':     'community',
    };
    const tab = pathToTab[location.pathname];
    if (tab) setActiveTab(tab);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [analyzing, setAnalyzing] = useState(false);
  
  // Load real trades data - Initialize FIRST
  const [trades, setTrades] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalPnL: 0,
    winningTrades: 0,
    losingTrades: 0,
    winRate: 0
  });
  
  const [showAlert, setShowAlert] = useState(true);
  const [liveAlerts, setLiveAlerts] = useState([]);

  // Capital for P&L percentage calculation (persisted in localStorage)
  const [capital, setCapital] = useState(() => {
    const saved = localStorage.getItem('tradefix_capital');
    const val = saved ? parseFloat(saved) : 100000;
    console.log('[Capital] Loaded from localStorage:', val);
    return val;
  });
  const [editingCapital, setEditingCapital] = useState(false);
  const [capitalInput, setCapitalInput] = useState('');

  // Generate AI Coach Suggestions based on real data
  const [aiCoachSuggestions, setAiCoachSuggestions] = useState([]);

  // Chat Widget State
  const [chatMinimized, setChatMinimized] = useState(true);

  // Quick Entry State
  const [voiceRecording, setVoiceRecording] = useState(false);

  // Subscription State — initialize from localStorage cache for instant render
  const _cachedPlan    = getPlanFromCache();
  const _cachedIsPaid  = _cachedPlan !== null && _cachedPlan !== 'free';
  const [subscription, setSubscription] = useState(null);
  const [isPaidUser, setIsPaidUser]     = useState(_cachedIsPaid);
  const [planLoading, setPlanLoading]   = useState(_cachedPlan === null); // only true on very first visit
  const [tradesRemaining, setTradesRemaining] = useState(5);
  const [streak, setStreak] = useState({ currentStreak: 0, longestStreak: 0 });
  const [monthlyProgress, setMonthlyProgress] = useState(null);
  const [aiCoachPopup, setAiCoachPopup] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [weeklyExpanded, setWeeklyExpanded] = useState(false);
  const [monthlyExpanded, setMonthlyExpanded] = useState(false);
  const [personalReport, setPersonalReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportExpanded, setReportExpanded] = useState(false);
  const [postTradeFeedback, setPostTradeFeedback] = useState(null);
  const [lossStreakAlert, setLossStreakAlert] = useState(null);

  // CSV Import State
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState('upload'); // 'upload' | 'preview' | 'importing' | 'success'
  const [importedTrades, setImportedTrades] = useState([]);
  const [importError, setImportError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [importCount, setImportCount] = useState(0);
  const [importSkipped, setImportSkipped] = useState(0);
  const [importBroker, setImportBroker] = useState('');
  const [importRawCount, setImportRawCount] = useState(0);

  const { totalPnL, winningTrades, losingTrades, winRate } = dashboardStats;

  // Function to load dashboard data from Firestore
  const loadDashboardData = async () => {
    if (!currentUser) return;

    console.log('[Dashboard] Fetching trades for user:', currentUser.id);
    const userTrades = await getTrades(currentUser.id);
    console.log('[Dashboard] Fetched', userTrades.length, 'trades for user:', currentUser.id);
    // Verify each trade belongs to this user
    const validTrades = userTrades.filter(t => {
      if (t.userId && t.userId !== currentUser.id) {
        console.warn('[Dashboard] Skipping trade with wrong userId:', t.userId, '(expected:', currentUser.id, ')');
        return false;
      }
      return true;
    });
    setTrades(validTrades);

    if (validTrades.length > 0) {
      const totalPnL = validTrades.reduce((acc, trade) => acc + trade.pnl, 0);
      const winningTrades = validTrades.filter(t => t.pnl > 0).length;
      const losingTrades = validTrades.filter(t => t.pnl < 0).length;
      const winRate = ((winningTrades / validTrades.length) * 100).toFixed(1);

      setDashboardStats({ totalPnL, winningTrades, losingTrades, winRate });
    } else {
      setDashboardStats({ totalPnL: 0, winningTrades: 0, losingTrades: 0, winRate: 0 });
    }
  };

  // Function to generate live alerts
  const generateLiveAlerts = async () => {
    if (!currentUser || trades.length === 0) {
      setLiveAlerts([]);
      return;
    }

    const alerts = [];
    const now = new Date();
    const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

    // Load risk settings from Firestore
    const riskSettings = (await getRiskSettings(currentUser.id)) || { dailyLossLimit: 5000, maxTradesPerDay: 5 };
    console.log('[Alerts] Generating alerts — trades:', trades.length, ', riskSettings:', riskSettings);

    // Calculate today's stats from actual trade data
    const today = new Date().toISOString().split('T')[0];
    const todayTrades = trades.filter(t => t.date === today);
    const todayLoss = todayTrades.filter(t => t.pnl < 0).reduce((sum, t) => sum + Math.abs(t.pnl), 0);
    const todayCount = todayTrades.length;

    // FOMO trades today (check emotion field)
    const fomoToday = todayTrades.filter(t =>
      t.emotion && t.emotion.toLowerCase().includes('fomo')
    ).length;

    // Alert 1: Daily loss limit
    if (todayLoss > 0) {
      const lossPercentage = (todayLoss / riskSettings.dailyLossLimit) * 100;
      if (lossPercentage >= 80) {
        alerts.push({
          id: 1,
          type: 'critical',
          message: `⚠️ Daily loss limit ${lossPercentage.toFixed(0)}% crossed! ₹${todayLoss.toFixed(0)} / ₹${riskSettings.dailyLossLimit}`,
          time: timeStr
        });
      } else if (lossPercentage >= 50) {
        alerts.push({
          id: 1,
          type: 'warning',
          message: `⚠️ Daily loss at ${lossPercentage.toFixed(0)}%: ₹${todayLoss.toFixed(0)} / ₹${riskSettings.dailyLossLimit}`,
          time: timeStr
        });
      }
    }

    // Alert 2: Overtrading — matches AI Insights detection logic
    if (todayCount > riskSettings.maxTradesPerDay) {
      alerts.push({
        id: 2,
        type: 'critical',
        message: `⚠️ Overtrading Alert: ${todayCount} trades today (limit: ${riskSettings.maxTradesPerDay})`,
        time: timeStr
      });
    } else if (todayCount === riskSettings.maxTradesPerDay) {
      alerts.push({
        id: 2,
        type: 'warning',
        message: `⚠️ Maximum trades reached: ${todayCount}/${riskSettings.maxTradesPerDay} trades today`,
        time: timeStr
      });
    } else if (todayCount >= Math.ceil(riskSettings.maxTradesPerDay * 0.8)) {
      alerts.push({
        id: 2,
        type: 'warning',
        message: `Trading frequency high: ${todayCount} of ${riskSettings.maxTradesPerDay} trades completed today`,
        time: timeStr
      });
    }

    // Alert 3: Win rate below 40%
    const winRateNum = parseFloat(dashboardStats.winRate);
    if (winRateNum < 40 && trades.length >= 10) {
      alerts.push({
        id: 3,
        type: 'warning',
        message: `⚠️ Win rate dropped below 40% (${dashboardStats.winRate}%) — Review your strategy`,
        time: 'Overall'
      });
    }

    // Alert 4: FOMO trades > 3 in a day
    if (fomoToday >= 3) {
      alerts.push({
        id: 4,
        type: 'warning',
        message: `⚠️ FOMO Alert: ${fomoToday} FOMO trades today — Step back and reassess!`,
        time: timeStr
      });
    }

    // Alert 5: Consecutive wins (last 3)
    const last3Trades = trades.slice(-3);
    if (last3Trades.length === 3 && last3Trades.every(t => t.pnl > 0)) {
      alerts.push({
        id: 5,
        type: 'success',
        message: '🎉 3 winning trades in a row — Great momentum!',
        time: 'Recent'
      });
    }

    // Alert 6: Consecutive losses (last 3)
    if (last3Trades.length === 3 && last3Trades.every(t => t.pnl < 0)) {
      alerts.push({
        id: 6,
        type: 'critical',
        message: '⚠️ 3 consecutive losses — Take a break and reset!',
        time: 'Recent'
      });
    }

    // Alert 7: Excellent win rate
    if (winRateNum >= 60 && trades.length >= 10) {
      alerts.push({
        id: 7,
        type: 'success',
        message: `🎯 Excellent win rate: ${dashboardStats.winRate}% — Keep it up!`,
        time: 'Overall'
      });
    }

    // Default if no alerts generated
    if (alerts.length === 0) {
      alerts.push({
        id: 8,
        type: 'success',
        message: '✅ All risk parameters within limits — Safe to trade!',
        time: 'Now'
      });
    }

    console.log('[Alerts] Generated', alerts.length, 'alert(s):', alerts.map(a => a.message));
    setLiveAlerts(alerts.slice(0, 5));
  };

  // Function to generate AI coach suggestions
  const generateAICoachSuggestions = () => {
    if (!currentUser || trades.length === 0) {
      setAiCoachSuggestions([]);
      return;
    }
    
    const suggestions = [];
    
    // Analyze recent trades
    const recentTrades = trades.slice(-5);
    const recentPnL = recentTrades.reduce((sum, t) => sum + t.pnl, 0);
    
    // Suggestion 1: Based on win rate
    if (dashboardStats.winRate < 50 && trades.length >= 5) {
      suggestions.push({
        type: 'warning',
        message: `Win rate ${dashboardStats.winRate}% है। Entry strategy improve करनी होगी - confluence देखें`,
        confidence: 88
      });
    } else if (dashboardStats.winRate >= 60) {
      suggestions.push({
        type: 'positive',
        message: `Excellent! ${dashboardStats.winRate}% win rate बहुत अच्छा है। Consistency maintain करें`,
        confidence: 92
      });
    } else {
      suggestions.push({
        type: 'neutral',
        message: `Win rate ${dashboardStats.winRate}% decent है। Small improvements से 60%+ achieve कर सकते हैं`,
        confidence: 85
      });
    }
    
    // Suggestion 2: Based on recent performance
    if (recentPnL > 0 && recentTrades.length >= 3) {
      suggestions.push({
        type: 'positive',
        message: `Last ${recentTrades.length} trades में ₹${recentPnL.toFixed(0)} profit - good momentum है!`,
        confidence: 90
      });
    } else if (recentPnL < 0 && recentTrades.length >= 3) {
      suggestions.push({
        type: 'warning',
        message: `Last ${recentTrades.length} trades में loss है। Break लें, strategy reassess करें`,
        confidence: 95
      });
    }
    
    // Suggestion 3: Based on trade frequency
    const today = new Date().toISOString().split('T')[0];
    const todayCount = trades.filter(t => t.date === today).length;
    
    if (todayCount >= 5) {
      suggestions.push({
        type: 'warning',
        message: `आज already ${todayCount} trades हो चुके हैं। Quality over quantity - patience रखें`,
        confidence: 87
      });
    } else if (todayCount === 0 && trades.length > 0) {
      suggestions.push({
        type: 'neutral',
        message: 'आज कोई trades नहीं। Good setups का wait करें, forced entries नहीं',
        confidence: 80
      });
    }
    
    // Suggestion 4: Based on risk:reward
    const avgWin = dashboardStats.totalPnL > 0 && winningTrades > 0
      ? trades.filter(t => t.pnl > 0).reduce((sum, t) => sum + t.pnl, 0) / winningTrades
      : 0;
    const avgLoss = losingTrades > 0
      ? trades.filter(t => t.pnl < 0).reduce((sum, t) => sum + t.pnl, 0) / losingTrades
      : 0;
    const riskReward = avgLoss !== 0 ? Math.abs(avgWin / avgLoss) : 0;
    
    if (riskReward < 1.5 && trades.length >= 5) {
      suggestions.push({
        type: 'warning',
        message: `Risk:Reward 1:${riskReward.toFixed(2)} है। Targets बड़े रखें, minimum 1:2 target करें`,
        confidence: 90
      });
    } else if (riskReward >= 2) {
      suggestions.push({
        type: 'positive',
        message: `Risk:Reward 1:${riskReward.toFixed(2)} excellent है! यह maintain करें`,
        confidence: 93
      });
    }
    
    // Suggestion 5: Based on emotions (if available)
    const emotionTrades = trades.filter(t => t.emotion);
    if (emotionTrades.length >= 5) {
      const emotionStats = {};
      emotionTrades.forEach(t => {
        if (!emotionStats[t.emotion]) {
          emotionStats[t.emotion] = { wins: 0, total: 0 };
        }
        emotionStats[t.emotion].total++;
        if (t.pnl > 0) emotionStats[t.emotion].wins++;
      });
      
      const bestEmotion = Object.entries(emotionStats)
        .sort((a, b) => (b[1].wins / b[1].total) - (a[1].wins / a[1].total))[0];
      
      if (bestEmotion) {
        const emotionWinRate = ((bestEmotion[1].wins / bestEmotion[1].total) * 100).toFixed(0);
        suggestions.push({
          type: 'positive',
          message: `"${bestEmotion[0]}" state में ${emotionWinRate}% win rate - इस mental state में trade करें`,
          confidence: 89
        });
      }
    }
    
    // Keep only top 3 suggestions
    setAiCoachSuggestions(suggestions.slice(0, 3));
  };

  // Per-trade AI Coach analysis — pure function, no state reads
  const analyzeTradeWithAICoach = (newTrade, allTrades) => {
    const insights = [];
    const BAD_EMOTIONS = ['FOMO', 'Revenge', 'Greedy', 'Fearful', 'Anxious'];

    // 1. Entry/exit move analysis
    const move = Math.abs((newTrade.exit - newTrade.entry) / newTrade.entry * 100);
    if (move < 0.5 && newTrade.pnl > 0) {
      insights.push({ type: 'warning', text: `Small move captured (${move.toFixed(2)}%) on ${newTrade.stock}. You may be cutting profits too early — let winners run further.` });
    } else if (move > 3) {
      insights.push({ type: 'positive', text: `Strong ${move.toFixed(1)}% move captured on ${newTrade.stock}. Excellent patience holding through the trend!` });
    }

    // 2. Emotion with historical context
    const emotionHistory = allTrades.filter(t => t.emotion === newTrade.emotion && t.id !== newTrade.id);
    if (BAD_EMOTIONS.includes(newTrade.emotion)) {
      if (emotionHistory.length >= 2) {
        const lossRate = ((emotionHistory.filter(t => t.pnl < 0).length / emotionHistory.length) * 100).toFixed(0);
        insights.push({ type: 'warning', text: `"${newTrade.emotion}" state — you lose ${lossRate}% of trades in this mindset. Try trading only when Calm or Confident.` });
      } else {
        insights.push({ type: 'warning', text: `You traded while "${newTrade.emotion}". This emotional state reduces decision quality. Take a 10-min pause before the next trade.` });
      }
    } else if (emotionHistory.length >= 2) {
      const winRate = ((emotionHistory.filter(t => t.pnl > 0).length / emotionHistory.length) * 100).toFixed(0);
      insights.push({ type: 'positive', text: `"${newTrade.emotion}" is your strong state — ${winRate}% win rate historically. Keep trading with this mindset!` });
    }

    // 3. Consecutive loss / overtrading pattern
    const sortedHistory = allTrades
      .filter(t => t.id !== newTrade.id)
      .sort((a, b) => new Date(a.createdAt || a.date) - new Date(b.createdAt || b.date));
    const recent3 = sortedHistory.slice(-3);
    const recentLosses = recent3.filter(t => t.pnl < 0).length;

    if (recentLosses >= 3 && newTrade.pnl < 0) {
      insights.push({ type: 'critical', text: `You tend to overtrade after consecutive losses — 4 losses in a row. Take a mandatory break. Revenge trading is setting in.` });
    } else if (recentLosses >= 2 && newTrade.pnl < 0) {
      insights.push({ type: 'warning', text: `3 consecutive losses detected. Step away and review your setup — don't chase losses.` });
    } else if (recent3.length >= 2 && recent3.every(t => t.pnl > 0) && newTrade.pnl < 0) {
      insights.push({ type: 'warning', text: `You gave back profits after a winning streak on ${newTrade.stock}. Consider reducing size after a big run.` });
    }

    // 4. Trade result summary (always shown)
    if (newTrade.pnl > 0) {
      insights.push({ type: 'positive', text: `₹${newTrade.pnl.toFixed(0)} profit on ${newTrade.stock} ${newTrade.type}. Review what you did right and replicate it.` });
    } else {
      insights.push({ type: 'info', text: `₹${Math.abs(newTrade.pnl).toFixed(0)} loss on ${newTrade.stock} ${newTrade.type}. Losses happen — focus on whether the setup was valid, not just the outcome.` });
    }

    return insights.slice(0, 3);
  };

  // Generate "Your Personal Trading Report" using Smart AI with pattern detection
  const generatePersonalReport = async (userTrades) => {
    if (reportLoading || personalReport) return;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const recent = userTrades.filter(t => t.date && new Date(t.date) >= cutoff);
    if (recent.length < 3) return;

    setReportLoading(true);
    try {
      const groqFn = httpsCallable(getFunctions(app), 'groqChat');
      const userName = auth.currentUser?.displayName || currentUser?.displayName || 'Trader';
      const smartPrompt = buildSmartPrompt(recent, userName);
      if (!smartPrompt) return;

      const result = await groqFn({
        messages: [{ role: 'user', content: smartPrompt }]
      });
      setPersonalReport(result.data.reply);
    } catch (err) {
      console.error('[PersonalReport] Error:', err);
    } finally {
      setReportLoading(false);
    }
  };

  // Generate report when dashboard loads with enough trades
  useEffect(() => {
    if (currentUser && trades.length >= 3 && !personalReport && !reportLoading) {
      generatePersonalReport(trades);
    }
  }, [currentUser, trades.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for loss streaks whenever trades update
  const checkLossStreak = (tradeList) => {
    const sorted = [...tradeList].sort((a,b) => new Date(b.date) - new Date(a.date));
    let streak = 0;
    for (const trade of sorted) {
      if (trade.pnl < 0) streak++;
      else break;
    }
    return streak;
  };

  useEffect(() => {
    if (trades.length > 0) {
      const streak = checkLossStreak(trades);
      if (streak >= 3) setLossStreakAlert(streak);
      else setLossStreakAlert(null);
    }
  }, [trades]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for new trades from TradeEntry — trigger AI Coach popup + post-trade AI feedback
  useEffect(() => {
    const handler = async (e) => {
      if (!currentUser) return;
      const { trade, allTrades } = e.detail;

      // Existing local AI coach analysis
      const insights = analyzeTradeWithAICoach(trade, allTrades);
      await saveAiInsight(currentUser.id, {
        tradeId: trade.id,
        stock: trade.stock,
        pnl: trade.pnl,
        emotion: trade.emotion,
        insights
      });
      setAiCoachPopup({ trade, insights });

      // Post-trade AI feedback via Groq
      try {
        const groqFn = httpsCallable(getFunctions(app), 'groqChat');
        const userName = auth.currentUser?.displayName || 'Trader';
        const prompt = buildPostTradePrompt(trade, allTrades.slice(-5), userName);
        const result = await groqFn({ messages: [{ role: 'user', content: prompt }] });
        if (result.data?.reply) setPostTradeFeedback(result.data.reply);
      } catch (err) {
        console.error('[PostTradeFeedback] Error:', err);
      }
    };
    window.addEventListener('tradefix-trade-logged', handler);
    return () => window.removeEventListener('tradefix-trade-logged', handler);
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load trades and subscription whenever currentUser changes or tab switches
  useEffect(() => {
    if (currentUser) {
      const load = async () => {
        await loadDashboardData();
        const [sub, paid, remaining] = await Promise.all([
          getUserSubscription(currentUser.id),
          hasActiveSubscription(currentUser.id),
          getRemainingTrades(currentUser.id),
        ]);
        setSubscription(sub);
        setIsPaidUser(paid);
        setTradesRemaining(remaining);
        // Update persistent cache so next load is instant
        const planId = sub?.planId || (paid ? 'pro' : 'free');
        setPlanCache(planId);
        setPlanLoading(false);
      };
      load();
    } else {
      setPlanLoading(false);
    }
  }, [currentUser, activeTab]);

  // Generate alerts when trades or stats change
  useEffect(() => {
    if (currentUser && trades.length > 0) {
      generateLiveAlerts();
    }
  }, [currentUser, trades, dashboardStats]);

  // Generate AI suggestions when trades or stats change
  useEffect(() => {
    if (currentUser && trades.length > 0) {
      generateAICoachSuggestions();
    }
  }, [currentUser, trades, dashboardStats]);

  // Compute and persist monthly progress stats
  useEffect(() => {
    if (!currentUser || trades.length === 0) return;

    const now = new Date();
    const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonth = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;

    const BAD_EMOTIONS = ['FOMO', 'Revenge', 'Greedy', 'Fearful', 'Anxious'];
    const GOOD_EMOTIONS = ['Calm', 'Confident'];

    const monthTrades = trades.filter(t => t.date && t.date.startsWith(thisMonth));
    const wins = monthTrades.filter(t => t.pnl > 0).length;
    const monthWinRate = monthTrades.length > 0 ? parseFloat(((wins / monthTrades.length) * 100).toFixed(1)) : 0;

    // Psychology score: % of trades with positive emotion, scaled 0-100
    const emotionTrades = monthTrades.filter(t => t.emotion);
    const goodCount = emotionTrades.filter(t => GOOD_EMOTIONS.includes(t.emotion)).length;
    const psychologyScore = emotionTrades.length > 0
      ? Math.round((goodCount / emotionTrades.length) * 100)
      : 50; // neutral default when no emotion data

    // Most common mistake: most frequent bad emotion in losing trades
    const losingWithEmotion = monthTrades.filter(t => t.pnl < 0 && t.emotion && BAD_EMOTIONS.includes(t.emotion));
    const emotionCount = {};
    losingWithEmotion.forEach(t => { emotionCount[t.emotion] = (emotionCount[t.emotion] || 0) + 1; });
    const topMistake = Object.keys(emotionCount).sort((a, b) => emotionCount[b] - emotionCount[a])[0] || 'None detected';

    const statsToSave = { month: thisMonth, totalTrades: monthTrades.length, winRate: monthWinRate, psychologyScore, topMistake };

    saveMonthlyStats(currentUser.id, thisMonth, statsToSave);

    // Fetch last month's stats for psychology score comparison
    getMonthlyStats(currentUser.id, lastMonth).then(last => {
      setMonthlyProgress({ ...statsToSave, lastPsychScore: last?.psychologyScore ?? null });
    });
  }, [currentUser, trades]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate weekly (Monday) and monthly (1st) reports
  useEffect(() => {
    if (!currentUser || trades.length === 0) return;

    const BAD_EMOTIONS = ['FOMO', 'Revenge', 'Greedy', 'Fearful', 'Anxious'];
    const GOOD_EMOTIONS = ['Calm', 'Confident'];

    const psychScore = (tradeList) => {
      const e = tradeList.filter(t => t.emotion);
      return e.length > 0
        ? Math.round((e.filter(t => GOOD_EMOTIONS.includes(t.emotion)).length / e.length) * 100)
        : null;
    };

    const topMistakes = (tradeList) => {
      const counts = {};
      tradeList.filter(t => t.pnl < 0 && t.emotion && BAD_EMOTIONS.includes(t.emotion))
        .forEach(t => { counts[t.emotion] = (counts[t.emotion] || 0) + 1; });
      return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3)
        .map(([emotion, count]) => ({ emotion, count }));
    };

    const run = async () => {
      const now = new Date();
      const meta = await getReportMeta(currentUser.id);

      // ── Weekly report — runs on Monday ──────────────────────────
      if (now.getDay() === 1) {
        const weekKey = getISOWeekKey(now);
        if (meta.lastWeeklyReport !== weekKey) {
          const lastSun = new Date(now); lastSun.setDate(now.getDate() - 1);
          const lastMon = new Date(now); lastMon.setDate(now.getDate() - 7);
          const wStart = lastMon.toISOString().split('T')[0];
          const wEnd   = lastSun.toISOString().split('T')[0];
          const wTrades = trades.filter(t => t.date >= wStart && t.date <= wEnd);

          if (wTrades.length > 0) {
            const wins = wTrades.filter(t => t.pnl > 0).length;
            const winRate = ((wins / wTrades.length) * 100).toFixed(1);

            // Best / worst day by P&L
            const dayPnL = {};
            wTrades.forEach(t => { dayPnL[t.date] = (dayPnL[t.date] || 0) + t.pnl; });
            const daySorted = Object.entries(dayPnL).sort((a, b) => b[1] - a[1]);
            const bestDay  = daySorted[0];
            const worstDay = daySorted[daySorted.length - 1];

            const top3 = topMistakes(wTrades);
            const pScore = psychScore(wTrades);

            // Rule-based improvement tip
            const topEmotion = top3[0]?.emotion;
            let tip = 'Journal your thoughts before each trade to improve decision quality.';
            if (topEmotion === 'FOMO')
              tip = 'Before entering, ask: "Is this my pre-planned setup or am I chasing?" Only trade what you planned.';
            else if (topEmotion === 'Revenge')
              tip = 'Implement a mandatory 30-min break after any loss — this one rule can save thousands.';
            else if (topEmotion === 'Greedy')
              tip = 'Set a fixed target before entering. Once reached, exit — never let greed override your plan.';
            else if (parseFloat(winRate) < 40)
              tip = 'Focus on entry quality — wait for at least 2 confluences before pulling the trigger.';
            else if (parseFloat(winRate) >= 60)
              tip = 'Excellent week! Consider slightly increasing position size to compound your edge.';
            else if (wTrades.length > 25)
              tip = 'You overtraded this week. Limit yourself to 3–5 high-quality setups per day.';

            // Fetch previous week's snapshot for comparison
            const prevWeekDate = new Date(now); prevWeekDate.setDate(now.getDate() - 7);
            const prevWeekKey = getISOWeekKey(prevWeekDate);
            const prevWeekStats = await getWeeklyStats(currentUser.id, prevWeekKey);
            const winRateDiff = prevWeekStats?.winRate != null ? parseFloat(winRate) - parseFloat(prevWeekStats.winRate) : null;
            const tradesDiff = prevWeekStats?.totalTrades != null ? wTrades.length - prevWeekStats.totalTrades : null;
            const wPsychDiff = pScore !== null && prevWeekStats?.psychScore != null ? pScore - prevWeekStats.psychScore : null;

            setWeeklyReport({ weekKey, period: `${wStart} to ${wEnd}`, totalTrades: wTrades.length, winRate, bestDay, worstDay, psychScore: pScore, top3Mistakes: top3, tip, winRateDiff, tradesDiff, psychDiff: wPsychDiff, prevTopMistake: prevWeekStats?.topMistake ?? null, prevWinRate: prevWeekStats?.winRate ?? null, prevTotalTrades: prevWeekStats?.totalTrades ?? null });
            // Save this week's snapshot for next week's comparison
            await saveWeeklyStats(currentUser.id, weekKey, { totalTrades: wTrades.length, winRate, psychScore: pScore, topMistake: top3[0]?.emotion || 'None' });
            await saveReportMeta(currentUser.id, { lastWeeklyReport: weekKey });
          }
        }
      }

      // ── Monthly report — runs on 1st of month ───────────────────
      if (now.getDate() === 1) {
        const thisMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        if (meta.lastMonthlyReport !== thisMonthKey) {
          const lmDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const lmKey  = `${lmDate.getFullYear()}-${String(lmDate.getMonth() + 1).padStart(2, '0')}`;
          const lmTrades = trades.filter(t => t.date && t.date.startsWith(lmKey));

          if (lmTrades.length > 0) {
            const wins = lmTrades.filter(t => t.pnl > 0).length;
            const winRate   = ((wins / lmTrades.length) * 100).toFixed(1);
            const totalPnL  = lmTrades.reduce((s, t) => s + t.pnl, 0);
            const pScore    = psychScore(lmTrades);
            const lmTop3    = topMistakes(lmTrades);

            // Max consecutive trading days this month
            const tradeDays = [...new Set(lmTrades.map(t => t.date))].sort();
            let maxMonthStreak = tradeDays.length > 0 ? 1 : 0; let msCur = 1;
            for (let i = 1; i < tradeDays.length; i++) {
              const diff = (new Date(tradeDays[i]) - new Date(tradeDays[i - 1])) / 86400000;
              if (diff === 1) { msCur++; if (msCur > maxMonthStreak) maxMonthStreak = msCur; }
              else msCur = 1;
            }

            // Emotional trading losses this month
            const emotionalLossTrades = lmTrades.filter(t => t.pnl < 0 && BAD_EMOTIONS.includes(t.emotion || ''));
            const emotionalLossAmount = emotionalLossTrades.reduce((s, t) => s + Math.abs(t.pnl), 0);

            // Compare with the month before last
            const prevDate  = new Date(now.getFullYear(), now.getMonth() - 2, 1);
            const prevKey   = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
            const prevStats = await getMonthlyStats(currentUser.id, prevKey);
            const psychDiff = pScore !== null && prevStats?.psychologyScore != null ? pScore - prevStats.psychologyScore : null;
            const tradesDiff = prevStats?.totalTrades != null ? lmTrades.length - prevStats.totalTrades : null;
            const winRateDiff = prevStats?.winRate != null ? parseFloat(winRate) - parseFloat(prevStats.winRate) : null;
            const streakDiff = prevStats?.longestStreak != null ? maxMonthStreak - prevStats.longestStreak : null;
            const emotionalSaved = prevStats?.emotionalLossAmount != null ? prevStats.emotionalLossAmount - emotionalLossAmount : null;
            const emotionalCountSaved = prevStats?.emotionalTradeCount != null ? prevStats.emotionalTradeCount - emotionalLossTrades.length : null;

            // Save this month's full snapshot for next month's comparison
            await saveMonthlyStats(currentUser.id, lmKey, {
              month: lmKey, totalTrades: lmTrades.length, winRate, psychologyScore: pScore,
              topMistake: lmTop3[0]?.emotion || 'None', longestStreak: maxMonthStreak,
              emotionalLossAmount, emotionalTradeCount: emotionalLossTrades.length
            });

            setMonthlyReport({
              monthKey: lmKey,
              month: lmDate.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
              totalTrades: lmTrades.length, winRate, totalPnL, psychScore: pScore,
              psychDiff, tradesDiff, winRateDiff, streakDiff,
              longestStreak: maxMonthStreak,
              emotionalSaved, emotionalCountSaved,
              topMistake: lmTop3[0]?.emotion || null
            });
            await saveReportMeta(currentUser.id, { lastMonthlyReport: thisMonthKey });
          }
        }
      }
    };

    run();
  }, [currentUser, trades]); // eslint-disable-line react-hooks/exhaustive-deps

  // Voice Recording Handler
  const toggleVoiceRecording = () => {
    setVoiceRecording(!voiceRecording);
    if (!voiceRecording) {
      setTimeout(() => {
        setVoiceRecording(false);
        alert('Trade logged: INFY Buy @ 1450, Qty: 10');
      }, 3000);
    }
  };

  // Save capital to state + localStorage
  const handleCapitalSave = () => {
    const num = parseFloat(capitalInput) || 100000;
    setCapital(num);
    localStorage.setItem('tradefix_capital', num.toString());
    setEditingCapital(false);
    console.log('[Capital] Saved to localStorage:', num);
  };

  // Check if current user is on Elite plan
  const isEliteUser = isPaidUser && (subscription?.planId === 'elite' || subscription?.planId === 'premium');

  // P&L color helper — uses CSS variables for light/dark theme support
  const getPnlColor = (value) => {
    if (value > 0) return 'var(--profit)';
    if (value < 0) return 'var(--loss)';
    return 'var(--text-secondary)';
  };

  const handleCSVFile = (file) => {
    setImportError(null);
    if (!file || !file.name.toLowerCase().endsWith('.csv')) {
      setImportError('Please upload a valid .csv file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = parseBrokerCSV(e.target.result);
        if (result.completedTrades.length === 0) {
          throw new Error('No completed trades found. Make sure your CSV has both BUY and SELL entries for the same symbol on the same date.');
        }
        setImportBroker(result.broker);
        setImportRawCount(result.rawTrades.length);
        setImportedTrades(result.completedTrades);
        setImportStep('preview');
      } catch (err) {
        setImportError(err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleImportConfirm = async () => {
    if (!currentUser || importedTrades.length === 0) return;
    setImportStep('importing');
    try {
      const existingKeys = new Set(
        trades.map(t => `${t.symbol}_${t.date}_${t.quantity}`)
      );
      let imported = 0;
      let skipped = 0;

      for (const trade of importedTrades) {
        const key = `${trade.symbol}_${trade.date}_${trade.quantity}`;
        if (existingKeys.has(key)) { skipped++; continue; }

        await saveTrade(currentUser.id, {
          stock: trade.symbol,
          symbol: trade.symbol,
          date: trade.date,
          type: trade.type === 'BUY' ? 'Buy' : 'Sell',
          quantity: trade.quantity,
          entry: trade.entryPrice,
          exit: trade.exitPrice,
          pnl: trade.pnl,
          emotion: trade.emotion || 'Neutral',
          notes: trade.notes || `Imported from ${trade.broker}`,
          setup: trade.setup || 'Imported',
          imported: true,
          userId: currentUser.id,
          createdAt: new Date().toISOString(),
        });
        imported++;
      }

      setImportCount(imported);
      setImportSkipped(skipped);
      setImportStep('success');
      await loadDashboardData();
    } catch (err) {
      setImportError('Import failed: ' + err.message);
      setImportStep('preview');
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportStep('upload');
    setImportedTrades([]);
    setImportError(null);
    setIsDragOver(false);
    setImportBroker('');
    setImportRawCount(0);
    setImportSkipped(0);
  };

  const StatCard = ({ title, value, change, icon: Icon, positive }) => (
    <div className="rounded-xl p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderLeft: `3px solid ${positive ? 'var(--profit)' : 'var(--loss)'}` }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: 11 }}>{title}</p>
          <p className="text-2xl font-bold mt-1.5" style={{ color: 'var(--text-primary)', fontWeight: 800 }}>{value}</p>
          {change && (
            <p className="text-sm mt-1 font-medium" style={{ color: positive ? 'var(--profit)' : 'var(--loss)' }}>
              {change}
            </p>
          )}
        </div>
        <div className="p-3 rounded-xl" style={{ background: positive ? 'var(--success-bg)' : 'var(--danger-bg)' }}>
          <Icon size={22} style={{ color: positive ? 'var(--profit)' : 'var(--loss)' }} />
        </div>
      </div>
    </div>
  );

  // Public policy pages — accessible regardless of auth state
  if (location.pathname === '/refund') return <RefundPolicy />;
  if (location.pathname === '/terms') return <TermsAndConditions />;
  if (location.pathname === '/privacy') return <PrivacyPolicy />;

  // Pricing page — accessible for logged-in users inside Layout
  if (location.pathname === '/pricing' && isAuthenticated) {
    return (
      <Layout currentUser={currentUser} isPaidUser={isPaidUser} onLogout={handleLogout}>
        <PricingPage />
      </Layout>
    );
  }

  // Show loading screen while Firebase resolves auth (prevents white/black flash)
  if (!authReady) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0d1117'
      }}>
        <div style={{
          color: '#00d084',
          fontSize: '18px',
          fontWeight: '700',
          letterSpacing: '1px'
        }}>
          TradeFix AI...
        </div>
      </div>
    );
  }

  // Show public routes when not logged in
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Auth mode="login" onLogin={handleLogin} />} />
        <Route path="/signup" element={<Auth mode="signup" onLogin={handleLogin} />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/refund" element={<RefundPolicy />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Layout currentUser={currentUser} isPaidUser={isPaidUser} onLogout={handleLogout}>
      {/* Live Alerts Banner */}
      {showAlert && liveAlerts.length > 0 && liveAlerts[0] && (
        <div className="py-2.5 px-4 flex items-center justify-between" style={{
          background: liveAlerts[0].type === 'critical' ? 'var(--danger-bg)' :
                      liveAlerts[0].type === 'warning'  ? 'var(--warning-bg)' :
                      'var(--success-bg)',
          borderBottom: '1px solid var(--border)',
          borderLeft: `3px solid ${liveAlerts[0].type === 'critical' ? 'var(--danger)' : liveAlerts[0].type === 'warning' ? 'var(--warning)' : 'var(--success)'}`
        }}>
          <div className="flex items-center space-x-3">
            <BellRing size={18} className="animate-pulse" style={{ color: liveAlerts[0].type === 'critical' ? 'var(--danger)' : liveAlerts[0].type === 'warning' ? 'var(--warning)' : 'var(--success)' }} />
            <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{liveAlerts[0].message}</span>
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{liveAlerts[0].time}</span>
          </div>
          <button onClick={() => setShowAlert(false)} style={{ color: 'var(--text-secondary)' }}>
            <XCircle size={18} />
          </button>
        </div>
      )}

      {location.pathname === '/affiliate' ? (
        <AffiliatePage currentUser={currentUser} />
      ) : (
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-5">

            {/* ── Loss Streak Alert ── */}
            {lossStreakAlert >= 3 && (
              <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>🚨</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: 'var(--danger)', fontSize: 14 }}>
                    {lossStreakAlert} consecutive losses!
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    Break lo. Screen band karo. Kal fresh mind se aana.
                  </div>
                </div>
                <button onClick={() => setLossStreakAlert(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>×</button>
              </div>
            )}

            {/* ── Free Plan Upgrade Banner (paid users see nothing here) ── */}
            {!planLoading && !isPaidUser && (
              <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderTop: '3px solid var(--accent)' }}>
                <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-subtle)', border: '1px solid var(--accent-glow)' }}>
                      <Crown size={22} style={{ color: 'var(--accent)' }} />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-0.5">
                        <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--text-primary)' }}>Free Plan</span>
                        {isInTrialPeriod(currentUser) && (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'var(--warning-bg)', color: 'var(--warning)', border: '1px solid var(--warning)' }}>
                            TRIAL
                          </span>
                        )}
                      </div>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {tradesRemaining === 0
                          ? 'Trade limit reached — upgrade to continue'
                          : `${10 - tradesRemaining} of 10 free trades used this month`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-bold text-sm whitespace-nowrap transition-all"
                    style={{ background: 'var(--accent)', color: '#000', border: 'none' }}
                  >
                    <Crown size={14} />
                    <span>Upgrade to Pro — ₹999/mo</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
                <div className="px-5 py-3 grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ borderTop: '1px solid var(--border)' }}>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Trade Usage</span>
                      <span className="text-xs font-bold" style={{ color: tradesRemaining === 0 ? 'var(--loss)' : 'var(--accent)' }}>
                        {10 - tradesRemaining}/10 used
                      </span>
                    </div>
                    <div className="w-full rounded-full h-2" style={{ background: 'var(--border)' }}>
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{
                          width: `${Math.min(((10 - tradesRemaining) / 10) * 100, 100)}%`,
                          background: tradesRemaining === 0 ? 'var(--danger)' : tradesRemaining <= 2 ? 'var(--warning)' : 'var(--accent)'
                        }}
                      />
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                      {tradesRemaining === 0 ? 'Upgrade for unlimited trades' : `${tradesRemaining} trades remaining`}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>Locked Features:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {['AI Insights', 'Psychology Tracker', 'Risk Manager'].map(f => (
                        <span key={f} className="flex items-center space-x-1 text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-card-hover)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                          <Lock size={9} />
                          <span>{f}</span>
                        </span>
                      ))}
                    </div>
                    <p className="text-xs mt-1.5" style={{ color: 'var(--text-secondary)' }}>14-day money-back guarantee</p>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {(() => {
                const todayTrades = trades.filter(t => new Date(t.date).toDateString() === new Date().toDateString());
                const todayPnl = todayTrades.reduce((s, t) => s + (t.pnl || 0), 0);
                return (
                  <div className="rounded-xl p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Today's P&L</p>
                      <Calendar size={16} style={{ color: 'var(--accent)', opacity: 0.7 }} />
                    </div>
                    <p className="text-2xl font-bold" style={{ color: todayPnl > 0 ? 'var(--profit)' : todayPnl < 0 ? 'var(--loss)' : 'var(--text-secondary)' }}>
                      {todayPnl >= 0 ? '+' : ''}₹{Math.abs(todayPnl).toLocaleString()}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{todayTrades.length} trade{todayTrades.length !== 1 ? 's' : ''} today</p>
                  </div>
                );
              })()}
              <StatCard
                title="Total P&L"
                value={`₹${totalPnL.toLocaleString()}`}
                change={capital > 0
                  ? `${totalPnL >= 0 ? '+' : ''}${((totalPnL / capital) * 100).toFixed(1)}% of capital`
                  : 'Set capital to see %'}
                icon={DollarSign}
                positive={totalPnL > 0}
              />
              <StatCard
                title="Win Rate"
                value={`${trades.length > 0 ? winRate : 0}%`}
                change={`${winningTrades}W / ${losingTrades}L`}
                icon={Target}
                positive={parseFloat(winRate) >= 50}
              />
              <StatCard
                title="Total Trades"
                value={trades.length}
                change={trades.length > 0 ? `${trades.length} trades logged` : 'No trades yet'}
                icon={ListChecks}
                positive={true}
              />
              <div
                className="stat-card upload-card"
                onClick={() => isEliteUser ? setShowImportModal(true) : navigate('/pricing')}
              >
                <div className="stat-label">QUICK IMPORT</div>
                <div className="upload-card-content">
                  <Upload size={24} style={{ color: 'var(--accent)' }} />
                  <span className="upload-card-text">Upload CSV</span>
                </div>
                <div className="stat-sublabel">{isEliteUser ? 'Import trades' : 'Elite only'}</div>
              </div>
            </div>

            {/* Streak Widget */}
            {(() => {
              const s = streak.currentStreak;
              const msg = s >= 15 ? 'Trading champion!' : s >= 8 ? "You're on fire!" : s >= 4 ? 'Great start!' : 'Build your habit!';
              const accentColor = s >= 15 ? 'var(--warning)' : s >= 8 ? 'var(--warning)' : s >= 4 ? 'var(--profit)' : 'var(--accent)';
              return (
                <div className="rounded-xl p-5 flex items-center justify-between" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                  <div className="flex items-center space-x-4">
                    <span className="text-3xl">🔥</span>
                    <div>
                      <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{s} Day Streak{s === 1 ? '' : '!'}</p>
                      <p className="text-sm" style={{ color: accentColor }}>{msg}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Longest streak</p>
                    <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{streak.longestStreak} days</p>
                  </div>
                </div>
              );
            })()}

            {/* Progress Tracking Widget */}
            {monthlyProgress && (
              <div className="rounded-xl p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <div className="flex items-center space-x-2 mb-4">
                  <Calendar size={18} style={{ color: 'var(--accent)' }} />
                  <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Your Progress This Month</h3>
                  <span className="ml-auto text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{monthlyProgress.month}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="rounded-lg p-3 text-center" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
                    <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>{monthlyProgress.totalTrades}</p>
                    <p className="text-xs mt-1 font-medium" style={{ color: 'var(--text-muted)' }}>Trades Logged</p>
                  </div>
                  <div className="rounded-lg p-3 text-center" style={{ background: monthlyProgress.winRate >= 50 ? 'var(--success-bg)' : 'var(--danger-bg)', border: `1px solid ${monthlyProgress.winRate >= 50 ? 'var(--profit)' : 'var(--loss)'}` }}>
                    <p className="text-2xl font-bold" style={{ color: monthlyProgress.winRate >= 50 ? 'var(--profit)' : 'var(--loss)' }}>{monthlyProgress.winRate}%</p>
                    <p className="text-xs mt-1 font-medium" style={{ color: monthlyProgress.winRate >= 50 ? 'var(--profit)' : 'var(--loss)' }}>Win Rate</p>
                  </div>
                  <div className="rounded-lg p-3 text-center" style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)' }}>
                    <p className="text-sm font-bold leading-tight" style={{ color: 'var(--warning)' }}>{monthlyProgress.topMistake}</p>
                    <p className="text-xs mt-1 font-medium" style={{ color: 'var(--warning)' }}>Top Mistake</p>
                  </div>
                  <div className="rounded-lg p-3 text-center" style={{ background: 'var(--accent-subtle)', border: '1px solid var(--accent-glow)' }}>
                    {monthlyProgress.lastPsychScore !== null ? (
                      <>
                        <p className="text-2xl font-bold" style={{ color: monthlyProgress.psychologyScore >= monthlyProgress.lastPsychScore ? 'var(--profit)' : 'var(--loss)' }}>
                          {monthlyProgress.psychologyScore >= monthlyProgress.lastPsychScore ? '+' : ''}
                          {(monthlyProgress.psychologyScore - monthlyProgress.lastPsychScore).toFixed(0)}%
                        </p>
                        <p className="text-xs mt-1 font-medium" style={{ color: 'var(--accent)' }}>Psych Improvement</p>
                      </>
                    ) : (
                      <>
                        <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>{monthlyProgress.psychologyScore}</p>
                        <p className="text-xs mt-1 font-medium" style={{ color: 'var(--accent)' }}>Psych Score</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Weekly Report Card */}
            {weeklyReport && (
              <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <button
                  onClick={() => setWeeklyExpanded(e => !e)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left transition-colors hover:opacity-80"
                  style={{ background: 'var(--bg-card-hover)' }}
                >
                  <div className="flex items-center space-x-3">
                    <FileText size={18} style={{ color: 'var(--accent)' }} />
                    <div>
                      <p className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Weekly Comparison Report</p>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{weeklyReport.period}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: 'var(--accent-subtle)', color: 'var(--accent)' }}>
                      {weeklyExpanded ? 'Collapse ↑' : 'Expand ↓'}
                    </span>
                  </div>
                </button>
                {weeklyExpanded && (
                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="rounded-lg p-3 text-center" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
                        <p className="text-xl font-bold" style={{ color: 'var(--accent)' }}>{weeklyReport.totalTrades}</p>
                        <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--text-muted)' }}>Total Trades</p>
                        {weeklyReport.tradesDiff !== null && (
                          <p className="text-xs font-semibold mt-1" style={{ color: weeklyReport.tradesDiff >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                            {weeklyReport.tradesDiff >= 0 ? '↑' : '↓'} {weeklyReport.tradesDiff >= 0 ? '+' : ''}{weeklyReport.tradesDiff} vs last wk
                          </p>
                        )}
                      </div>
                      <div className="rounded-lg p-3 text-center" style={{ background: parseFloat(weeklyReport.winRate) >= 50 ? 'var(--success-bg)' : 'var(--danger-bg)', border: `1px solid ${parseFloat(weeklyReport.winRate) >= 50 ? 'var(--profit)' : 'var(--loss)'}` }}>
                        <p className="text-xl font-bold" style={{ color: parseFloat(weeklyReport.winRate) >= 50 ? 'var(--profit)' : 'var(--loss)' }}>{weeklyReport.winRate}%</p>
                        <p className="text-xs mt-0.5 font-medium" style={{ color: parseFloat(weeklyReport.winRate) >= 50 ? 'var(--profit)' : 'var(--loss)' }}>Win Rate</p>
                        {weeklyReport.winRateDiff !== null && (
                          <p className="text-xs font-semibold mt-1" style={{ color: weeklyReport.winRateDiff >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                            {weeklyReport.winRateDiff >= 0 ? '↑' : '↓'} {weeklyReport.winRateDiff >= 0 ? '+' : ''}{weeklyReport.winRateDiff.toFixed(1)}% vs last wk
                          </p>
                        )}
                      </div>
                      <div className="rounded-lg p-3 text-center" style={{ background: 'var(--success-bg)', border: '1px solid var(--profit)' }}>
                        <p className="text-xs font-bold truncate" style={{ color: 'var(--profit)' }}>{weeklyReport.bestDay?.[0] || '—'}</p>
                        <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--profit)' }}>Best Day</p>
                        {weeklyReport.bestDay && <p className="text-sm font-bold" style={{ color: 'var(--profit)' }}>₹{weeklyReport.bestDay[1].toFixed(0)}</p>}
                      </div>
                      <div className="rounded-lg p-3 text-center" style={{ background: 'var(--danger-bg)', border: '1px solid var(--loss)' }}>
                        <p className="text-xs font-bold truncate" style={{ color: 'var(--loss)' }}>{weeklyReport.worstDay?.[0] || '—'}</p>
                        <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--loss)' }}>Worst Day</p>
                        {weeklyReport.worstDay && <p className="text-sm font-bold" style={{ color: 'var(--loss)' }}>₹{weeklyReport.worstDay[1].toFixed(0)}</p>}
                      </div>
                    </div>
                    {weeklyReport.psychScore !== null && (
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-semibold flex items-center space-x-1" style={{ color: 'var(--text-primary)' }}>
                            <Brain size={13} style={{ color: 'var(--accent)' }} />
                            <span>Psychology Score This Week</span>
                          </span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-bold" style={{ color: 'var(--accent)' }}>{weeklyReport.psychScore}/100</span>
                            {weeklyReport.psychDiff !== null && (
                              <span className="text-xs font-bold" style={{ color: weeklyReport.psychDiff >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                                {weeklyReport.psychDiff >= 0 ? '↑' : '↓'} {weeklyReport.psychDiff >= 0 ? '+' : ''}{weeklyReport.psychDiff}pts vs last wk
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="w-full rounded-full h-2" style={{ background: 'var(--border)' }}>
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${weeklyReport.psychScore}%`,
                              background: weeklyReport.psychScore >= 70 ? 'var(--profit)' : weeklyReport.psychScore >= 40 ? 'var(--warning)' : 'var(--danger)'
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {weeklyReport.top3Mistakes.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Top Mistakes This Week</p>
                        <div className="flex flex-wrap gap-2">
                          {weeklyReport.top3Mistakes.map((m, i) => (
                            <span key={i} className="text-xs font-medium px-3 py-1 rounded-full" style={{ background: 'var(--warning-bg)', color: 'var(--warning)', border: '1px solid var(--warning)' }}>
                              {m.emotion} × {m.count}
                            </span>
                          ))}
                        </div>
                        {weeklyReport.prevTopMistake && weeklyReport.prevTopMistake !== 'None' && (
                          <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                            Last week: <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{weeklyReport.prevTopMistake}</span>
                            {weeklyReport.top3Mistakes[0]?.emotion === weeklyReport.prevTopMistake
                              ? <span style={{ color: 'var(--loss)' }} className="ml-1">— still repeating!</span>
                              : <span style={{ color: 'var(--profit)' }} className="ml-1">— improved!</span>
                            }
                          </p>
                        )}
                      </div>
                    )}
                    <div className="rounded-lg p-3 flex items-start space-x-2" style={{ background: 'var(--accent-subtle)', border: '1px solid var(--accent-glow)' }}>
                      <Sparkles size={14} style={{ color: 'var(--accent)' }} className="flex-shrink-0 mt-0.5" />
                      <p className="text-sm" style={{ color: 'var(--text-primary)' }}><span style={{ fontWeight: 600, color: 'var(--accent)' }}>AI Tip: </span>{weeklyReport.tip}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Monthly Report Card */}
            {monthlyReport && (
              <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <button
                  onClick={() => setMonthlyExpanded(e => !e)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left transition-colors hover:opacity-80"
                  style={{ background: 'var(--bg-card-hover)' }}
                >
                  <div className="flex items-center space-x-3">
                    <Trophy size={18} style={{ color: 'var(--profit)' }} />
                    <div>
                      <p className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Monthly Comparison Report</p>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{monthlyReport.month}</p>
                    </div>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: 'var(--accent-subtle)', color: 'var(--accent)' }}>
                    {monthlyExpanded ? 'Collapse ↑' : 'Expand ↓'}
                  </span>
                </button>
                {monthlyExpanded && (
                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="rounded-lg p-3 text-center" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
                        <p className="text-xl font-bold" style={{ color: 'var(--accent)' }}>{monthlyReport.totalTrades}</p>
                        <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--text-muted)' }}>Trades</p>
                        {monthlyReport.tradesDiff !== null && (
                          <p className="text-xs font-semibold mt-1" style={{ color: monthlyReport.tradesDiff >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                            {monthlyReport.tradesDiff >= 0 ? '↑' : '↓'} {monthlyReport.tradesDiff >= 0 ? '+' : ''}{monthlyReport.tradesDiff} vs last mo
                          </p>
                        )}
                      </div>
                      <div className="rounded-lg p-3 text-center" style={{ background: parseFloat(monthlyReport.winRate) >= 50 ? 'var(--success-bg)' : 'var(--danger-bg)', border: `1px solid ${parseFloat(monthlyReport.winRate) >= 50 ? 'var(--profit)' : 'var(--loss)'}` }}>
                        <p className="text-xl font-bold" style={{ color: parseFloat(monthlyReport.winRate) >= 50 ? 'var(--profit)' : 'var(--loss)' }}>{monthlyReport.winRate}%</p>
                        <p className="text-xs mt-0.5 font-medium" style={{ color: parseFloat(monthlyReport.winRate) >= 50 ? 'var(--profit)' : 'var(--loss)' }}>Win Rate</p>
                        {monthlyReport.winRateDiff !== null && (
                          <p className="text-xs font-semibold mt-1" style={{ color: monthlyReport.winRateDiff >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                            {monthlyReport.winRateDiff >= 0 ? '↑' : '↓'} {monthlyReport.winRateDiff >= 0 ? '+' : ''}{monthlyReport.winRateDiff.toFixed(1)}% vs last mo
                          </p>
                        )}
                      </div>
                      <div className="rounded-lg p-3 text-center" style={{ background: monthlyReport.totalPnL >= 0 ? 'var(--success-bg)' : 'var(--danger-bg)', border: `1px solid ${monthlyReport.totalPnL >= 0 ? 'var(--profit)' : 'var(--loss)'}` }}>
                        <p className="text-xl font-bold" style={{ color: monthlyReport.totalPnL >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                          {monthlyReport.totalPnL >= 0 ? '+' : ''}₹{Math.abs(monthlyReport.totalPnL).toFixed(0)}
                        </p>
                        <p className="text-xs mt-0.5 font-medium" style={{ color: monthlyReport.totalPnL >= 0 ? 'var(--profit)' : 'var(--loss)' }}>Total P&L</p>
                      </div>
                      <div className="rounded-lg p-3 text-center" style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)' }}>
                        <p className="text-xl font-bold" style={{ color: 'var(--warning)' }}>🔥 {monthlyReport.longestStreak}d</p>
                        <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--warning)' }}>Month Streak</p>
                        {monthlyReport.streakDiff !== null && (
                          <p className="text-xs font-semibold mt-1" style={{ color: monthlyReport.streakDiff >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                            {monthlyReport.streakDiff >= 0 ? '↑' : '↓'} {monthlyReport.streakDiff >= 0 ? '+' : ''}{monthlyReport.streakDiff}d vs last mo
                          </p>
                        )}
                      </div>
                    </div>
                    {monthlyReport.psychScore !== null && (
                      <div className="flex items-center justify-between rounded-lg p-3" style={{ background: 'var(--accent-subtle)', border: '1px solid var(--accent-glow)' }}>
                        <div className="flex items-center space-x-2">
                          <Brain size={15} style={{ color: 'var(--accent)' }} />
                          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Psychology Score</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{monthlyReport.psychScore}</span>
                          {monthlyReport.psychDiff !== null && (
                            <span className="text-sm font-bold" style={{ color: monthlyReport.psychDiff >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                              {monthlyReport.psychDiff >= 0 ? '↑ Improved' : '↓ Declined'} by {Math.abs(monthlyReport.psychDiff)} pts
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {monthlyReport.emotionalSaved != null && monthlyReport.emotionalSaved > 0 && (
                      <div className="flex items-center justify-between rounded-lg p-3" style={{ background: 'var(--success-bg)', border: '1px solid var(--profit)' }}>
                        <div className="flex items-center space-x-2">
                          <Shield size={15} style={{ color: 'var(--profit)' }} />
                          <div>
                            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Emotional Trading Savings</p>
                            {monthlyReport.emotionalCountSaved != null && monthlyReport.emotionalCountSaved > 0 && (
                              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Avoided {monthlyReport.emotionalCountSaved} emotional trade{monthlyReport.emotionalCountSaved !== 1 ? 's' : ''} vs last month</p>
                            )}
                          </div>
                        </div>
                        <span className="text-lg font-bold" style={{ color: 'var(--profit)' }}>≈ +₹{monthlyReport.emotionalSaved.toFixed(0)}</span>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        const text = `📊 My ${monthlyReport.month} Trading Report\n✅ ${monthlyReport.totalTrades} trades | ${monthlyReport.winRate}% win rate\n💰 P&L: ₹${monthlyReport.totalPnL.toFixed(0)}\n🧠 Psychology Score: ${monthlyReport.psychScore ?? 'N/A'}/100\n🔥 Month Streak: ${monthlyReport.longestStreak} days${monthlyReport.emotionalSaved > 0 ? `\n💚 Saved ≈ ₹${monthlyReport.emotionalSaved.toFixed(0)} by avoiding emotional trades` : ''}\n\nTracked with TradeFix AI`;
                        navigator.clipboard.writeText(text).then(() => alert('Progress copied to clipboard!'));
                      }}
                      className="w-full flex items-center justify-center space-x-2 py-2.5 rounded-xl font-semibold text-sm transition-colors"
                      style={{ background: 'var(--accent)', color: '#000', border: 'none' }}
                    >
                      <Trophy size={15} />
                      <span>Share Your Progress</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Live Alerts Section */}
            <div className="rounded-xl p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold flex items-center space-x-2" style={{ color: 'var(--text-primary)' }}>
                  <BellRing size={18} style={{ color: 'var(--accent)' }} />
                  <span>Live Alerts & Risk Violations</span>
                </h3>
              </div>
              {liveAlerts.length > 0 ? (
                <div className="space-y-2">
                  {liveAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="p-3 rounded-lg"
                      style={{
                        background: alert.type === 'critical' ? 'var(--danger-bg)' :
                                    alert.type === 'warning'  ? 'var(--warning-bg)' :
                                    'var(--success-bg)',
                        borderLeft: `3px solid ${alert.type === 'critical' ? 'var(--danger)' : alert.type === 'warning' ? 'var(--warning)' : 'var(--profit)'}`
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{alert.message}</p>
                        <span className="text-xs ml-2 flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>{alert.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6" style={{ color: 'var(--text-secondary)' }}>
                  <BellRing size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No alerts yet. Start trading to get live notifications!</p>
                </div>
              )}
            </div>

            {/* Recent Trades Quick View */}
            <div className="rounded-xl p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold flex items-center space-x-2" style={{ color: 'var(--text-primary)' }}>
                  <ListChecks size={18} style={{ color: 'var(--accent)' }} />
                  <span>Recent Trades</span>
                </h3>
                <button
                  onClick={() => setActiveTab('my-trades')}
                  className="text-xs font-semibold transition-colors hover:underline"
                  style={{ color: 'var(--accent)' }}
                >
                  View All →
                </button>
              </div>
              {trades.length === 0 ? (
                <div className="text-center py-8">
                  <ListChecks size={36} className="mx-auto mb-3 opacity-20" style={{ color: 'var(--text-muted)' }} />
                  <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>No trades yet. Add your first trade!</p>
                  <button
                    onClick={() => setActiveTab('my-trades')}
                    className="px-4 py-2 rounded-lg text-sm font-bold transition-opacity hover:opacity-80"
                    style={{ background: 'var(--accent)', color: '#000' }}
                  >
                    Add Trade
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {[...trades].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5).map((t, i) => (
                    <div
                      key={t.id || i}
                      onClick={() => setActiveTab('my-trades')}
                      className="flex items-center justify-between rounded-lg px-4 py-3 cursor-pointer transition-colors"
                      style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-primary)'}
                    >
                      <div>
                        <p className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{(t.stock || t.symbol || 'Unknown').toUpperCase()}</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t.date}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${(t.type || '').toUpperCase() === 'BUY' ? 'tag-buy' : 'tag-sell'}`}>
                          {t.type || 'Trade'}
                        </span>
                        {t.setup && (
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--accent-subtle)', color: 'var(--accent)' }}>
                            {t.setup}
                          </span>
                        )}
                      </div>
                      <p className="text-base font-extrabold" style={{ color: (t.pnl || 0) >= 0 ? 'var(--profit)' : 'var(--loss)', minWidth: 70, textAlign: 'right' }}>
                        {(t.pnl || 0) >= 0 ? '+' : ''}₹{Math.abs(t.pnl || 0).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mini P&L Chart — Last 30 Days */}
            {trades.length > 0 && (() => {
              const last30 = [];
              for (let i = 29; i >= 0; i--) {
                const d = new Date();
                d.setDate(d.getDate() - i);
                const dateStr = d.toDateString();
                const dayTrades = trades.filter(t => new Date(t.date).toDateString() === dateStr);
                const pnl = dayTrades.reduce((s, t) => s + (t.pnl || 0), 0);
                last30.push({
                  date: d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
                  pnl: Math.round(pnl),
                  trades: dayTrades.length,
                });
              }
              const hasData = last30.some(d => d.trades > 0);
              if (!hasData) return null;
              return (
                <div className="rounded-xl p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                  <div className="mb-3">
                    <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>P&L — Last 30 Days</h3>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Daily performance overview</p>
                  </div>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={last30} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                      <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} interval={4} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }}
                        formatter={(val, _, props) => [`₹${val >= 0 ? '+' : ''}${val} (${props.payload.trades} trades)`, 'P&L']}
                      />
                      <Bar dataKey="pnl" radius={[3, 3, 0, 0]}>
                        {last30.map((entry, i) => (
                          <Cell key={i} fill={entry.pnl >= 0 ? 'var(--profit)' : 'var(--loss)'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}

            {/* AI Coach Quick Tips */}
            <div className="rounded-xl p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--accent-subtle)' }}>
                  <Brain size={18} style={{ color: 'var(--accent)' }} />
                </div>
                <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>AI Coach Live Suggestions</h3>
              </div>
              {aiCoachSuggestions.length > 0 ? (
                <div className="space-y-2">
                  {aiCoachSuggestions.map((suggestion, idx) => (
                    <div key={idx} className="p-3 rounded-lg" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', borderLeft: '3px solid var(--accent)' }}>
                      <div className="flex items-start justify-between gap-3">
                        <p className="flex-1 text-sm" style={{ color: 'var(--text-primary)' }}>{suggestion.message}</p>
                        <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: 'var(--accent-subtle)', color: 'var(--accent)' }}>
                          {suggestion.confidence}% confident
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-5 rounded-lg text-center" style={{ background: 'var(--bg-primary)' }}>
                  <Brain size={40} className="mx-auto mb-3 opacity-40" style={{ color: 'var(--accent)' }} />
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>Add trades to get personalized AI suggestions!</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>मैं आपके trades analyze करके smart insights दूंगा</p>
                </div>
              )}
            </div>

            {/* Personal Trading Report — always visible, expandable */}
            {trades.length >= 2 && (
              <div style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '24px',
              }}>
                {/* Header — always visible */}
                <div
                  onClick={() => setReportExpanded(r => !r)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 20px',
                    cursor: 'pointer',
                    background: reportExpanded ? 'var(--bg-card-hover)' : 'var(--bg-card)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Brain size={18} style={{ color: 'var(--accent)' }} />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>
                        Your Personal Trading Report
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        Last 30 days · Powered by AI Coach
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--accent)', fontWeight: '600' }}>
                      {reportExpanded ? 'Collapse ↑' : 'View Report ↓'}
                    </span>
                  </div>
                </div>

                {/* Content — only when expanded */}
                {reportExpanded && (
                  <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
                    {reportLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-secondary)', fontSize: '14px', padding: '12px 0' }}>
                        <div className="spinner" />
                        Generating your report...
                      </div>
                    ) : personalReport ? (
                      <div className="ai-response-content">
                        <ReactMarkdown>{personalReport}</ReactMarkdown>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}

            {/* Quick Action Cards */}
            <div className="quick-actions-grid" style={{
              marginTop: '24px',
            }}>
              {[
                { icon: <ListChecks size={22} />, label: 'My Trades',    to: '/trades' },
                { icon: <Brain size={22} />,      label: 'AI Insights',  to: '/ai-analysis' },
                { icon: <Activity size={22} />,   label: 'Psychology',   to: '/journal' },
                { icon: <Shield size={22} />,     label: 'Risk Manager', to: '/risk-manager' },
                { icon: <Crown size={22} />,      label: isPaidUser ? 'Manage Plan' : 'Upgrade', to: '/pricing' },
              ].map((item) => (
                <div
                  key={item.label}
                  onClick={() => navigate(item.to)}
                  style={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: '12px',
                    padding: '20px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    minHeight: '90px',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = 'var(--accent)';
                    e.currentTarget.style.background = 'var(--accent-subtle)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'var(--bg-card)';
                  }}
                >
                  <div style={{ color: 'var(--accent)' }}>{item.icon}</div>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', textAlign: 'center' }}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Trades Tab */}
        {activeTab === 'my-trades' && (
          <TradeEntry currentUser={currentUser} onNavigate={setActiveTab} />
        )}

        {/* AI Insights Tab */}
        {activeTab === 'ai-insights' && (
          <AIInsights currentUser={currentUser} onNavigate={setActiveTab} />
        )}

        {/* Psychology Tab */}
        {activeTab === 'psychology' && (
          <PsychologyTracker currentUser={currentUser} onNavigate={setActiveTab} />
        )}

        {/* Risk Manager Tab */}
        {activeTab === 'risk' && (
          <RiskManager currentUser={currentUser} onNavigate={setActiveTab} />
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <Pricing currentUser={currentUser} onNavigate={setActiveTab} />
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <Reports currentUser={currentUser} />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <SettingsPage currentUser={currentUser} onNavigate={setActiveTab} />
        )}

        {/* Community Tab */}
        {activeTab === 'community' && (
          <Community />
        )}

        {/* Admin Tab */}
        {activeTab === 'admin' && ADMIN_EMAILS.includes(currentUser?.email) && (
          <AdminPanel currentUserEmail={currentUser?.email} />
        )}

        {/* Legal Pages */}
        {activeTab === 'terms' && (
          <TermsAndConditions onBack={() => setActiveTab('dashboard')} />
        )}
        {activeTab === 'privacy' && (
          <PrivacyPolicy onBack={() => setActiveTab('dashboard')} />
        )}
        {activeTab === 'refund' && (
          <RefundPolicy onBack={() => setActiveTab('dashboard')} />
        )}

        {/* Other tabs - Coming Soon */}
        {activeTab !== 'dashboard' && activeTab !== 'my-trades' && activeTab !== 'ai-insights' && activeTab !== 'psychology' && activeTab !== 'risk' && activeTab !== 'reports' && activeTab !== 'settings' && activeTab !== 'pricing' && activeTab !== 'admin' && activeTab !== 'terms' && activeTab !== 'privacy' && activeTab !== 'refund' && activeTab !== 'community' && (
          <div className="rounded-xl p-8 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <Brain size={48} className="mx-auto mb-4 opacity-60" style={{ color: 'var(--accent)' }} />
            <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{activeTab.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} Section</h3>
            <p style={{ color: 'var(--text-secondary)' }}>This section is under development. Coming soon!</p>
          </div>
        )}
      </div>
      )}

      {/* AI Assistant Widget */}
      <AIAssistant 
        currentUser={currentUser}
        minimized={chatMinimized}
        onToggleMinimize={() => setChatMinimized(!chatMinimized)}
      />

      {/* AI Coach Per-Trade Popup */}
      {/* Post-Trade AI Feedback Toast */}
      {postTradeFeedback && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, maxWidth: 320, background: 'var(--bg-card)', border: '1px solid var(--accent)', borderRadius: 12, padding: 16, zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 16 }}>🤖</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase' }}>AI Coach</span>
            <button onClick={() => setPostTradeFeedback(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18 }}>×</button>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5, margin: 0 }}>
            {postTradeFeedback}
          </p>
        </div>
      )}

      {aiCoachPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="rounded-2xl max-w-md w-full overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <div className="p-5" style={{ background: 'var(--bg-card-hover)', borderBottom: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--accent-subtle)' }}>
                    <Brain size={18} style={{ color: 'var(--accent)' }} />
                  </div>
                  <div>
                    <p className="font-bold" style={{ color: 'var(--text-primary)' }}>AI Coach has feedback</p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>on your latest trade</p>
                  </div>
                </div>
                <button onClick={() => setAiCoachPopup(null)} className="transition-colors" style={{ color: 'var(--text-secondary)' }}>
                  <XCircle size={20} />
                </button>
              </div>
              <div className="rounded-lg px-4 py-2.5 flex items-center justify-between text-sm" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{aiCoachPopup.trade.stock} · {aiCoachPopup.trade.type} · {aiCoachPopup.trade.emotion}</span>
                <span className="font-bold" style={{ color: getPnlColor(aiCoachPopup.trade.pnl) }}>
                  {aiCoachPopup.trade.pnl >= 0 ? '+' : ''}₹{aiCoachPopup.trade.pnl.toFixed(0)}
                </span>
              </div>
            </div>
            <div className="p-5 space-y-3">
              {aiCoachPopup.insights.map((insight, i) => (
                <div
                  key={i}
                  className="p-3.5 rounded-lg text-sm leading-relaxed"
                  style={{
                    background: insight.type === 'positive' ? 'var(--success-bg)' :
                                insight.type === 'critical' ? 'var(--danger-bg)' :
                                insight.type === 'warning'  ? 'var(--warning-bg)' :
                                'var(--accent-subtle)',
                    borderLeft: `3px solid ${insight.type === 'positive' ? 'var(--profit)' : insight.type === 'critical' ? 'var(--danger)' : insight.type === 'warning' ? 'var(--warning)' : 'var(--accent)'}`,
                    color: insight.type === 'positive' ? 'var(--profit)' : insight.type === 'critical' ? 'var(--loss)' : insight.type === 'warning' ? 'var(--warning)' : 'var(--accent)'
                  }}
                >
                  {insight.text}
                </div>
              ))}
            </div>
            <div className="px-5 pb-5">
              <button
                onClick={() => setAiCoachPopup(null)}
                className="w-full py-2.5 rounded-xl font-semibold transition-colors"
                style={{ background: 'var(--accent)', color: '#000', fontWeight: 700 }}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="rounded-2xl max-w-2xl w-full overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--accent-glow)', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
            <div className="p-5 flex items-center justify-between" style={{ background: 'var(--accent-subtle)', borderBottom: '1px solid var(--accent-glow)' }}>
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--accent-subtle)' }}>
                  <Upload size={18} style={{ color: 'var(--accent)' }} />
                </div>
                <div>
                  <p className="font-bold" style={{ color: 'var(--text-primary)' }}>Import Trades from Broker</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Supports Zerodha, Upstox, Angel One CSV formats</p>
                </div>
              </div>
              <button onClick={closeImportModal} style={{ color: 'var(--text-secondary)' }}>
                <XCircle size={20} />
              </button>
            </div>

            <div className="p-5">
              {importStep === 'upload' && (
                <div className="space-y-4">
                  <div
                    className="border-2 border-dashed rounded-xl p-10 text-center transition-colors"
                    style={{ borderColor: isDragOver ? 'var(--accent)' : 'var(--border)', background: isDragOver ? 'var(--accent-subtle)' : 'var(--bg-primary)' }}
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setIsDragOver(false); const file = e.dataTransfer.files[0]; handleCSVFile(file); }}
                  >
                    <Upload size={36} className="mx-auto mb-3 opacity-70" style={{ color: 'var(--accent)' }} />
                    <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Drag & drop your CSV file here</p>
                    <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>or click to browse</p>
                    <label className="cursor-pointer px-5 py-2 rounded-lg font-medium transition-colors" style={{ background: 'var(--accent)', color: '#000', fontWeight: 700 }}>
                      Browse File
                      <input type="file" accept=".csv" className="hidden" onChange={(e) => handleCSVFile(e.target.files[0])} />
                    </label>
                  </div>
                  {importError && (
                    <div className="rounded-lg p-3 text-sm flex items-start space-x-2" style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', color: 'var(--loss)' }}>
                      <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
                      <span>{importError}</span>
                    </div>
                  )}
                  <div className="rounded-lg p-4 text-sm" style={{ background: 'var(--accent-subtle)', border: '1px solid var(--accent-glow)' }}>
                    <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Supported Formats:</p>
                    <ul className="space-y-0.5" style={{ color: 'var(--text-secondary)' }}>
                      <li>• <strong style={{ color: 'var(--text-primary)' }}>Zerodha:</strong> Kite → Console → Tradebook</li>
                      <li>• <strong style={{ color: 'var(--text-primary)' }}>Upstox:</strong> Reports → Trade History</li>
                      <li>• <strong style={{ color: 'var(--text-primary)' }}>Angel One:</strong> Order History → Export CSV</li>
                    </ul>
                  </div>
                </div>
              )}

              {importStep === 'preview' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span style={{ background: 'var(--accent-subtle)', color: 'var(--accent)', padding: '3px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: 700 }}>
                      {importBroker === 'unknown' ? 'Generic Format' : `${importBroker.charAt(0).toUpperCase() + importBroker.slice(1)} Detected ✓`}
                    </span>
                    <button onClick={() => { setImportStep('upload'); setImportedTrades([]); }} className="text-sm underline" style={{ color: 'var(--text-secondary)' }}>← Back</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    <div className="rounded-lg p-3 text-center" style={{ background: 'var(--bg-primary)' }}>
                      <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-primary)' }}>{importRawCount}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Raw Entries</div>
                    </div>
                    <div className="rounded-lg p-3 text-center" style={{ background: 'var(--bg-primary)' }}>
                      <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--accent)' }}>{importedTrades.length}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Completed Trades</div>
                    </div>
                    <div className="rounded-lg p-3 text-center" style={{ background: 'var(--bg-primary)' }}>
                      <div style={{ fontSize: '20px', fontWeight: 800, color: importedTrades.reduce((s,t) => s + t.pnl, 0) >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                        ₹{importedTrades.reduce((s,t) => s + t.pnl, 0).toLocaleString('en-IN')}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Total P&L</div>
                    </div>
                  </div>
                  <div className="overflow-auto max-h-56 rounded-lg" style={{ border: '1px solid var(--border)' }}>
                    <table className="w-full text-xs">
                      <thead className="sticky top-0" style={{ background: 'var(--border-muted)' }}>
                        <tr>
                          <th className="px-3 py-2 text-left" style={{ color: 'var(--text-secondary)' }}>Date</th>
                          <th className="px-3 py-2 text-left" style={{ color: 'var(--text-secondary)' }}>Symbol</th>
                          <th className="px-3 py-2 text-right" style={{ color: 'var(--text-secondary)' }}>Qty</th>
                          <th className="px-3 py-2 text-right" style={{ color: 'var(--text-secondary)' }}>Entry</th>
                          <th className="px-3 py-2 text-right" style={{ color: 'var(--text-secondary)' }}>Exit</th>
                          <th className="px-3 py-2 text-right" style={{ color: 'var(--text-secondary)' }}>P&L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importedTrades.slice(0, 50).map((t, i) => (
                          <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                            <td className="px-3 py-1.5" style={{ color: 'var(--text-secondary)' }}>{t.date}</td>
                            <td className="px-3 py-1.5 font-bold" style={{ color: 'var(--text-primary)' }}>{t.symbol}</td>
                            <td className="px-3 py-1.5 text-right" style={{ color: 'var(--text-secondary)' }}>{t.quantity}</td>
                            <td className="px-3 py-1.5 text-right" style={{ color: 'var(--text-secondary)' }}>₹{t.entryPrice}</td>
                            <td className="px-3 py-1.5 text-right" style={{ color: 'var(--text-secondary)' }}>₹{t.exitPrice}</td>
                            <td className="px-3 py-1.5 text-right font-bold" style={{ color: t.pnl >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                              {t.pnl >= 0 ? '+' : ''}₹{t.pnl}
                            </td>
                          </tr>
                        ))}
                        {importedTrades.length > 50 && (
                          <tr><td colSpan={6} className="px-3 py-2 text-center text-xs" style={{ color: 'var(--text-secondary)' }}>... and {importedTrades.length - 50} more</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {importError && (
                    <div className="rounded-lg p-3 text-sm" style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', color: 'var(--loss)' }}>{importError}</div>
                  )}
                  <div className="flex space-x-3">
                    <button onClick={closeImportModal} className="flex-1 py-2.5 rounded-xl font-medium transition-colors" style={{ border: '1px solid var(--border)', color: 'var(--text-secondary)', background: 'transparent' }}>Cancel</button>
                    <button onClick={handleImportConfirm} className="flex-1 py-2.5 rounded-xl font-bold transition-colors" style={{ background: 'var(--accent)', color: '#000' }}>
                      Import {importedTrades.length} Trades →
                    </button>
                  </div>
                </div>
              )}

              {importStep === 'importing' && (
                <div className="py-10 text-center space-y-3">
                  <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}></div>
                  <p className="font-medium" style={{ color: 'var(--text-primary)' }}>Importing trades to your account...</p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Please wait</p>
                </div>
              )}

              {importStep === 'success' && (
                <div className="py-10 text-center space-y-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ background: 'var(--success-bg)' }}>
                    <CheckCircle size={36} style={{ color: 'var(--profit)' }} />
                  </div>
                  <p className="text-xl font-bold" style={{ color: 'var(--profit)' }}>Import Successful!</p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {importCount} trades imported{importSkipped > 0 ? `, ${importSkipped} duplicates skipped` : ''}
                  </p>
                  <button onClick={closeImportModal} className="px-8 py-2.5 rounded-xl font-bold transition-colors" style={{ background: 'var(--accent)', color: '#000' }}>Done</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdvancedTradingAnalyzer;
